import { toggleItemsInList } from 'shared-js/utils/array-utils'
import { publishBehavior } from './menus-publish-util'
import locationsService from '../../../locations/locations-service'
import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'
import _ from 'lodash'
import moment from 'moment'

export default {
  namespaced: true,
  state: {
    showStatus: false,
    locations: [],
    retrievingLocations: true,
    runningWorkflows: [],
    completedWorkflows: [],
    selectedLocations: [],
    publishingLocations: [],
    schedulingLocations: [],
    reschedulingLocations: [],
    revertingLocations: [],
    terminatingLocations: [],
    blobType: null
  },
  mutations: {
    SET_BLOB_TYPE (state, blobType) {
      state.blobType = blobType
    },
    SET_SHOW_STATUS (state, showStatus) {
      state.showStatus = showStatus
    },
    SET_LOCATIONS (state, locations) {
      state.locations = locations
    },
    RETRIEVING_LOCATIONS (state, retrieving) {
      state.retrievingLocations = retrieving
    },
    SET_MENUS (state, menus) {
      state.menus = menus
    },
    SET_LOCATIONS_SELECTED (state, { locations, selected }) {
      toggleItemsInList(state.selectedLocations, locations, selected)
    },
    CLEAR_LOCATIONS_SELECTION (state) {
      state.selectedLocations = []
    },
    SET_RUNNING_WORKFLOWS (state, status) {
      state.runningWorkflows = status
    },
    UPDATE_WORKFLOW_STATUS (state, workflow) {
      const index = state.runningWorkflows.findIndex(next => _.isEqual(next.wfExec, workflow.wfExec))

      if (workflow.syncCompleted) {
        if (index > -1) {
          state.runningWorkflows.splice(index, 1)
        }
        state.completedWorkflows.push(workflow)
      } else {
        if (index === -1) {
          state.runningWorkflows.push(workflow)
        } else {
          state.runningWorkflows.splice(index, 1, workflow)
        }
      }
    },
    SET_SCHEDULING_LOCATIONS (state, { locations, scheduling }) {
      toggleItemsInList(state.schedulingLocations, locations, scheduling)
    },
    SET_RESCHEDULING_LOCATIONS (state, { locations, rescheduling }) {
      toggleItemsInList(state.reschedulingLocations, locations, rescheduling)
    },
    SET_PUBLISHING_LOCATIONS (state, { locations, publishing }) {
      toggleItemsInList(state.publishingLocations, locations, publishing)
    },
    SET_REVERTING_LOCATIONS (state, { locations, reverting }) {
      toggleItemsInList(state.revertingLocations, locations, reverting)
    },
    SET_TERMINATING_LOCATIONS (state, { locations, terminating }) {
      toggleItemsInList(state.terminatingLocations, locations, terminating, (list, item) => list.findIndex(next => _.isEqual(next, item)))
    }
  },
  getters: {
    locationsRunningStatus (state) {
      const locationsStatus = {}

      for (const status of state.runningWorkflows) {
        for (const locationId of Object.keys(status.locationToStatuses)) {
          const entry = {
            ...status,
            states: status.locationToStatuses[[locationId]]
          }
          delete entry.locationToStatuses

          locationsStatus[locationId] = entry
        }
      }

      return locationsStatus
    },
    locationsCompletedStatus (state) {
      const locationsStatus = {}

      for (const status of state.completedWorkflows) {
        for (const locationId of Object.keys(status.locationToStatuses)) {
          const existing = locationsStatus[locationId]

          const states = status.locationToStatuses[[locationId]]
          const endTime = findEndTime(states)

          if (!existing || endTime === -1 || findEndTime(existing.states) < endTime) {
            locationsStatus[locationId] = { ...status, states, locationToStatuses: undefined }
          }
        }
      }

      return locationsStatus
    },
    locationsData (state, getters) {
      const data = {}

      for (let location of state.locations) {
        const status = getters.locationsRunningStatus[location.id]
        data[location.id] = {
          ...location,
          status,
          waitingStart: !_.get(status, 'syncStarted') && !_.get(status, 'syncCompleted'),
          hasChanges: state.blobType === 'menu' ? hasLocationMenuChanges(location) : hasLocationEmploymentChanges(location),
          isRunning: _.get(status, 'syncDate') === 0 || (_.get(status, 'syncStarted') && !_.get(state, 'syncCompleted')),
          isScheduled: moment(_.get(status, 'syncDate', 0)).isAfter(moment())
        }
      }

      return data
    },
    unpublishedLocations (state, getters) {
      return Object.values(getters.locationsData).filter(next => next.hasChanges && !next.isScheduled)
    },
    hasUnpublished (state, getters) {
      return getters.unpublishedLocations.length > 0
    }
  },
  actions: {
    setBlobType ({ commit }, blobType) {
      commit('SET_BLOB_TYPE', blobType)
    },
    setShowStatus ({ commit }, showStatus) {
      commit('SET_SHOW_STATUS', showStatus)
    },
    async retrieveData ({ dispatch }, blobType) {
      return Promise.all([dispatch('retrieveLocations'), dispatch('retrieveRunningWorkflows', blobType)])
    },
    async retrieveLocations ({ commit, state, rootState }) {
      try {
        commit('RETRIEVING_LOCATIONS', true)
        const companyId = rootState.login.user.userCompanyId
        const response = await axios.get(`/api/tenants/sync-status/${companyId}`)
        // Get the pos versions of the locations
        // (currently used to prevent employment publish for inadequate version)
        const allLocs = await locationsService.getLocations(companyId)
        response.data.forEach(location => {
          const realLoc = allLocs.find(l => l.id === location.id)
          if (realLoc) {
            location.thrivePosVersion = realLoc.thrivePosVersion
          }
        })
        commit('SET_LOCATIONS', response.data)
      } catch (e) {
        return exceptionHandler.parse(e)
      } finally {
        commit('RETRIEVING_LOCATIONS', false)
      }
    },
    async retrieveRunningWorkflows ({ commit, rootState }, blobType) {
      try {
        const response = await axios.get(`${publishBehavior(blobType).apiPath}/running/${rootState.login.user.userCompanyId}`)
        commit('SET_RUNNING_WORKFLOWS', response.data)
      } catch (e) {
        return exceptionHandler.parse(e)
      }
    },
    async updateWorkflowStatus ({ commit, dispatch }, { wfExec, locationIds, blobType }) {
      try {
        const response = await axios.post(`${publishBehavior(blobType).apiPath}/status`, wfExec)
        const workflow = { ...response.data, blobType }

        if (!workflow.syncCompleted && !workflow.syncStarted) {
          if (locationIds) {
            workflow.locationToStatuses = locationIds.reduce((acc, id) => ({ ...acc, [id]: [] }), {})
            commit('UPDATE_WORKFLOW_STATUS', workflow)
          }
        } else {
          commit('UPDATE_WORKFLOW_STATUS', workflow)
          if (workflow.syncCompleted) {
            return dispatch('retrieveLocations')
          }
        }
      } catch (e) {
        return exceptionHandler.parse(e)
      }
    },
    setLocationsSelected ({ commit }, { locations, selected }) {
      commit('SET_LOCATIONS_SELECTED', { locations, selected })
    },
    async publishNow ({ dispatch, commit }, { locationIds, blobType, action }) {
      commit('SET_PUBLISHING_LOCATIONS', { locations: locationIds, publishing: true })

      try {
        await dispatch('startWorkflow', { locationIds, blobType, action: 'PUBLISH' })
      } finally {
        commit('SET_PUBLISHING_LOCATIONS', { locations: locationIds, publishing: false })
      }
    },
    async revertNow ({ dispatch, commit }, { locationIds, blobType }) {
      commit('SET_REVERTING_LOCATIONS', { locations: locationIds, reverting: true })

      try {
        await dispatch('startWorkflow', { locationIds, blobType, action: 'REVERT' })
      } finally {
        commit('SET_REVERTING_LOCATIONS', { locations: locationIds, reverting: false })
      }
    },
    async schedule ({ commit, dispatch }, { locations, blobType, syncDateMillis = 0 }) {
      const locationIds = locations.map(next => next.id)
      commit('SET_SCHEDULING_LOCATIONS', { locations: locationIds, scheduling: true })

      try {
        await dispatch('startWorkflow', { locationIds, blobType, action: 'PUBLISH', syncDateMillis })
      } finally {
        commit('SET_SCHEDULING_LOCATIONS', { locations: locationIds, scheduling: false })
      }
    },
    // Start a publish workflow and let a failure REJECT, without the post-start status refresh that
    // startWorkflow bundles in. The Thrive Connect wizard (TPOS-10455) needs to distinguish a
    // publish-START failure (which must roll the enable back) from a later status-poll failure (which
    // must NOT), so it cannot use startWorkflow/publishNow — those reject on either and swallow the
    // distinction. Returns the workflow exec on success. Single call site; no chunking needed here.
    async startPublishOnly (ctx, { locationIds, blobType }) {
      const response = await axios.post(`${publishBehavior(blobType).apiPath}/start`, {
        syncDateMillis: 0,
        locationIds,
        action: 'PUBLISH'
      })
      return response.data
    },
    async startWorkflow ({ dispatch }, { locationIds, blobType, action, syncDateMillis = 0 }) {
      const chunkSize = 5
      const chunkDuration = 30 // seconds
      const startingMillis = syncDateMillis || new Date().getTime()
      try {
        const chunks = parseInt(locationIds.length / chunkSize) + (locationIds.length % chunkSize ? 1 : 0)
        for (let i = 0; i < chunks; i++) {
          const chunkLocationIds = locationIds.slice(i * chunkSize, i * chunkSize + chunkSize)
          // 1st chunk uses syncDateMillis, subsequent chunks calculate from startingMillis
          const chunkDateMillis = (i === 0 ? syncDateMillis : (startingMillis + chunkDuration * i * 1000))
          const response = await axios.post(`${publishBehavior(blobType).apiPath}/start`, {
            syncDateMillis: chunkDateMillis,
            locationIds: chunkLocationIds,
            action })
          const wfExec = response.data
          await dispatch('updateWorkflowStatus', { wfExec, locationIds: chunkLocationIds, blobType })
        }
      } catch (e) {
        return exceptionHandler.parse(e)
      }
    },
    async reschedule ({ state, dispatch, commit }, { locations, blobType, syncDateMillis = 0 }) {
      const locationIds = locations.map(next => next.id)
      commit('SET_RESCHEDULING_LOCATIONS', { locations: locationIds, rescheduling: true })
      const locationsByWorkflow = sortLocationsByWorkflow(locations)
      const { completeWorkflows, partialLocations, partialWorkflows } = sortWorkflowsWithAllLocations(locationsByWorkflow, state.runningWorkflows)

      let success = true

      for (const wfExec of completeWorkflows) {
        try {
          await axios.post(`${publishBehavior(blobType).apiPath}/reschedule`, { rescheduleTime: syncDateMillis, wfExec })
          await dispatch('updateWorkflowStatus', { wfExec, blobType })
        } catch (e) {
          success = false
        }
      }

      const successLocations = []
      for (const location of partialLocations) {
        try {
          await dispatch('terminate', location)
          successLocations.push({ id: location })
        } catch (e) {
          success = false
        }
      }

      if (successLocations.length > 0) {
        try {
          await dispatch('schedule', { locations: successLocations, syncDateMillis })
        } catch (e) {
          success = false
        }
      }

      for (const wfExec of partialWorkflows) {
        await dispatch('updateWorkflowStatus', { wfExec, blobType })
      }

      commit('SET_RESCHEDULING_LOCATIONS', { locations: locationIds, rescheduling: false })
      return success
    },
    async terminate ({ commit }, { locationId, blobType }) {
      commit('SET_TERMINATING_LOCATIONS', { locations: [locationId], terminating: true })
      try {
        await axios.post(`${publishBehavior(blobType).apiPath}/unschedule/${locationId}`)
      } catch (e) {
        return exceptionHandler.parse(e)
      } finally {
        commit('SET_TERMINATING_LOCATIONS', { locations: [locationId], terminating: false })
      }
    }
  }
}

const hasMismatch = (location, prop) => {
  const assignedVer = _.get(location, [prop, 'assigned', 'version'])
  const assignedId = _.get(location, [prop, 'assigned', 'id'])
  const publishedVer = _.get(location, [prop, 'published', 'version'])
  const publishedId = _.get(location, [prop, 'published', 'id'])
  const overrideAssignedVer = _.get(location, [prop, 'overrideAssigned', 'version'])
  const overrideAssignedId = _.get(location, [prop, 'overrideAssigned', 'id'])
  const overridePublishedVer = _.get(location, [prop, 'overridePublished', 'version'])
  const overridePublishedId = _.get(location, [prop, 'overridePublished', 'id'])
  const groupOverrideAssignedVer = _.get(location, [prop, 'groupOverrideAssigned', 'version'])
  const groupOverrideAssignedId = _.get(location, [prop, 'groupOverrideAssigned', 'id'])
  const groupOverridePublishedVer = _.get(location, [prop, 'groupOverridePublished', 'version'])
  const groupOverridePublishedId = _.get(location, [prop, 'groupOverridePublished', 'id'])

  const hasLocalMismatch = assignedVer
    ? assignedVer !== publishedVer || assignedId !== publishedId
    : false
  const hasOverrideMismatch = overrideAssignedVer
    ? overrideAssignedVer !== overridePublishedVer || overrideAssignedId !== overridePublishedId
    : false
  const hasGroupOverrideMismatch = groupOverrideAssignedVer
    ? groupOverrideAssignedVer !== groupOverridePublishedVer || groupOverrideAssignedId !== groupOverridePublishedId
    : false

  return hasLocalMismatch || hasOverrideMismatch || hasGroupOverrideMismatch
}

export function hasLocationMenuChanges (location) {
  return hasMismatch(location, 'menuInfo') || hasMismatch(location, 'locSettingsInfo')
}

export function hasLocationEmploymentChanges (location) {
  return hasMismatch(location, 'employmentInfo')
}

function findEndTime (statuses) {
  if (!statuses) return -1

  const last = statuses.find(next => next.terminal)
  return _.get(last, 'endTime', -1)
}

export function sortLocationsByWorkflow (locations) {
  const byWorkflow = []

  for (const location of locations) {
    let forWorkflow = byWorkflow.find(next => _.isEqual(next.wfExec, location.status.wfExec))
    if (!forWorkflow) {
      forWorkflow = { locations: [], wfExec: location.status.wfExec }
      byWorkflow.push(forWorkflow)
    }
    forWorkflow.locations.push(location.id)
  }

  return Object.values(byWorkflow)
}

export function sortWorkflowsWithAllLocations (locationsByWorkflow, runningWorkflows) {
  const completeWorkflows = []
  const partialLocations = []
  const partialWorkflows = []

  for (const entry of locationsByWorkflow) {
    const { locations, wfExec } = entry

    const workflow = runningWorkflows.find(next => _.isEqual(wfExec, next.wfExec))

    const difference = _.difference(Object.keys(workflow.locationToStatuses), locations)

    if (difference.length === 0) {
      completeWorkflows.push(entry.wfExec)
    } else {
      partialLocations.push(...locations)
      partialWorkflows.push(wfExec)
    }
  }

  return { completeWorkflows, partialLocations, partialWorkflows }
}
